import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import type {
  ProductScopeEntry,
  ProductVariantScope,
  SnoozeScope,
} from '../../hooks/useProductVariantScope';

type Presentation = 'desktop' | 'mobile';

interface VariantScopeListProps {
  scope: ProductVariantScope;
  /** Radio group on desktop/bulk, tap-target chips on mobile. */
  presentation?: Presentation;
  /** "Snoozing" on the snooze flows, "Activating" in the bulk activate flow. */
  verb?: string;
}

const SCOPE_OPTIONS: Array<{ value: SnoozeScope; label: string }> = [
  { value: 'WHOLE_PRODUCT', label: 'Whole product' },
  { value: 'SPECIFIC_VARIANTS', label: 'Specific variants only' },
];

/**
 * Per-product "Snoozing scope" drill-down. Every selected product with 2+
 * variants gets its own Whole product / Specific variants choice, so a
 * multi-item selection can mix the two — selecting a second item no longer
 * discards the first item's variant picks.
 *
 * Shared by the desktop item page, the mobile bottom sheet and the bulk editor.
 */
export const VariantScopeList: React.FC<VariantScopeListProps> = ({
  scope,
  presentation = 'desktop',
  verb = 'Snoozing',
}) => {
  const { entries, scopedEntries, setScope, toggleVariant } = scope;

  const loadingEntries = entries.filter((entry) => entry.isLoading);
  const singleVariantEntries = entries.filter((entry) => entry.showSingleVariantHint);

  if (
    loadingEntries.length === 0 &&
    singleVariantEntries.length === 0 &&
    scopedEntries.length === 0
  ) {
    return null;
  }

  return (
    <Stack spacing={1.5}>
      {loadingEntries.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 0.5 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            {loadingEntries.length === 1
              ? `Checking ${loadingEntries[0].name} for variants…`
              : `Checking ${loadingEntries.length} products for variants…`}
          </Typography>
        </Stack>
      )}

      {singleVariantEntries.length > 0 && (
        <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
          {verb} <strong>{singleVariantEntries.map((entry) => entry.name).join(', ')}</strong> in
          full
          {singleVariantEntries.length === 1
            ? ' — this product only has one variant on the menu.'
            : ' — these products only have one variant each on the menu.'}
        </Alert>
      )}

      {scopedEntries.length > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}
          >
            {presentation === 'mobile' ? 'SNOOZING SCOPE' : 'Snoozing scope'}
          </Typography>
          <Stack spacing={1.5}>
            {scopedEntries.map((entry) => (
              <ProductScopeRow
                key={entry.code}
                entry={entry}
                presentation={presentation}
                verb={verb}
                onScopeChange={setScope}
                onToggleVariant={toggleVariant}
                // Only label each row when there's more than one to tell apart.
                showProductName={scopedEntries.length > 1}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

interface ProductScopeRowProps {
  entry: ProductScopeEntry;
  presentation: Presentation;
  verb: string;
  showProductName: boolean;
  onScopeChange: (productCode: string, scope: SnoozeScope) => void;
  onToggleVariant: (productCode: string, variant: ProductScopeEntry['variants'][number]) => void;
}

const ProductScopeRow: React.FC<ProductScopeRowProps> = ({
  entry,
  presentation,
  verb,
  showProductName,
  onScopeChange,
  onToggleVariant,
}) => {
  const isSpecific = entry.scope === 'SPECIFIC_VARIANTS';

  return (
    <Box>
      {showProductName && (
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          {entry.name}
        </Typography>
      )}

      {presentation === 'mobile' ? (
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {SCOPE_OPTIONS.map((option) => (
            <Chip
              key={option.value}
              label={
                option.value === 'WHOLE_PRODUCT'
                  ? `Whole product (all ${entry.variants.length})`
                  : option.label
              }
              size="small"
              variant={entry.scope === option.value ? 'filled' : 'outlined'}
              color={entry.scope === option.value ? 'primary' : 'default'}
              onClick={() => onScopeChange(entry.code, option.value)}
              sx={{ fontWeight: entry.scope === option.value ? 600 : 400 }}
            />
          ))}
        </Stack>
      ) : (
        <FormControl component="fieldset">
          <RadioGroup
            value={entry.scope}
            onChange={(e) => onScopeChange(entry.code, e.target.value as SnoozeScope)}
          >
            <FormControlLabel
              value="WHOLE_PRODUCT"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  Whole product
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 0.5 }}
                  >
                    — all {entry.variants.length} variants
                  </Typography>
                </Typography>
              }
            />
            <FormControlLabel
              value="SPECIFIC_VARIANTS"
              control={<Radio size="small" />}
              label={<Typography variant="body2">Specific variants only</Typography>}
            />
          </RadioGroup>
        </FormControl>
      )}

      {isSpecific && (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: presentation === 'mobile' ? 2 : 1,
            bgcolor: 'action.hover',
            borderLeft: 3,
            borderColor: 'primary.main',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Pick one or more variants of {entry.name}. Other variants will stay available.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={presentation === 'mobile' ? 0.75 : 1}>
            {entry.variants.map((variant) => {
              const isSelected = entry.selectedVariants.some((v) => v.code === variant.code);
              return (
                <Chip
                  key={variant.code}
                  label={variant.name}
                  size={presentation === 'mobile' ? 'small' : 'medium'}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  clickable
                  onClick={() => onToggleVariant(entry.code, variant)}
                  sx={{ fontWeight: isSelected ? 600 : 400 }}
                />
              );
            })}
          </Stack>

          {entry.isInvalid ? (
            <Alert severity="error" icon={false} sx={{ mt: 1, py: 0.5 }}>
              Pick at least one variant of <strong>{entry.name}</strong>, or switch it to{' '}
              <strong>Whole product</strong>.
            </Alert>
          ) : (
            <Alert severity="warning" icon={false} sx={{ mt: 1, py: 0.5 }}>
              {verb}{' '}
              <strong>
                {entry.selectedVariants.length} of {entry.variants.length} variants
              </strong>{' '}
              of {entry.name} ({entry.selectedVariants.map((v) => v.name).join(', ')}). The other
              variants will remain available to order.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};
